// Shared primitives for the Fatigue Management dashboard.
//
// Rebuilt on shadcn/ui + scoped Tailwind (src/styles/fatigue-shadcn.css) to
// match ROI Reporting: flat, bordered, no glass, no glow, no entry motion.
// FM-5 — the previous versions leaned on box-shadow glows and an animated sweep
// on every red item, which was unreadable once several reds were listed at once.

import React from 'react';
import { AlertTriangle, ArrowDown, ArrowRight, ArrowUp, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DerivedMetricKey, FatigueRiskTier, TrendDirection } from '../data/types';
import { RAG_RANGES, formatBand } from '../data/methodology';

// ── Risk tier styling ───────────────────────────────────────────────────────
//
// One place decides what green/amber/red look like. Colours come from the CSS
// variables in fatigue-shadcn.css, so they follow light/dark automatically.

export const TIER_LABEL: Record<FatigueRiskTier, string> = {
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
};

export const tierTextClass: Record<FatigueRiskTier, string> = {
  green: 'text-success',
  amber: 'text-warning',
  red: 'text-destructive',
};

export const tierBorderClass: Record<FatigueRiskTier, string> = {
  green: 'border-success/40',
  amber: 'border-warning/40',
  red: 'border-destructive/40',
};

export const tierSurfaceClass: Record<FatigueRiskTier, string> = {
  green: 'bg-success/8',
  amber: 'bg-warning/8',
  red: 'bg-destructive/8',
};

export const tierDotClass: Record<FatigueRiskTier, string> = {
  green: 'bg-success',
  amber: 'bg-warning',
  red: 'bg-destructive',
};

/** CSS colour value for a tier, for SVG fills and recharts strokes. */
export const tierVar: Record<FatigueRiskTier, string> = {
  green: 'var(--color-success)',
  amber: 'var(--color-warning)',
  red: 'var(--color-destructive)',
};

// ── TierBadge ───────────────────────────────────────────────────────────────

export const TierBadge: React.FC<{
  tier: FatigueRiskTier;
  /** Optional descriptive suffix, e.g. "Act now". */
  caption?: string;
  className?: string;
}> = ({ tier, caption, className }) => (
  <span
    role='status'
    aria-label={`Risk tier ${TIER_LABEL[tier]}${caption ? `: ${caption}` : ''}`}
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold',
      tierBorderClass[tier],
      tierSurfaceClass[tier],
      tierTextClass[tier],
      className,
    )}
  >
    <span className={cn('size-2 rounded-full', tierDotClass[tier])} />
    {caption ?? TIER_LABEL[tier]}
  </span>
);

// ── TrendPill ───────────────────────────────────────────────────────────────

export const TrendPill: React.FC<{
  trend: TrendDirection;
  label: string;
  /** Which direction is the healthy one for this metric. */
  goodDirection: 'up' | 'down';
}> = ({ trend, label, goodDirection }) => {
  const isGood = trend === 'stable' ? null : (trend === 'up') === (goodDirection === 'up');
  const tone =
    isGood === null
      ? 'text-muted-foreground bg-muted'
      : isGood
        ? 'text-success bg-success/10'
        : 'text-destructive bg-destructive/10';
  const Icon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : ArrowRight;

  return (
    <span className={cn('inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', tone)}>
      <Icon className='size-3' aria-hidden />
      {label}
    </span>
  );
};

// ── SectionHeading ──────────────────────────────────────────────────────────

export const SectionHeading: React.FC<{
  overline?: string;
  title: string;
  subtitle?: string;
  /** Right-aligned controls, e.g. a block navigator. */
  actions?: React.ReactNode;
}> = ({ overline, title, subtitle, actions }) => (
  <div className='flex flex-wrap items-end justify-between gap-3'>
    <div className='min-w-0'>
      {overline && <p className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{overline}</p>}
      <h2 className='text-base font-semibold'>{title}</h2>
      {subtitle && <p className='mt-0.5 text-sm text-muted-foreground'>{subtitle}</p>}
    </div>
    {actions}
  </div>
);

// ── RiskAlert (FM-5) ────────────────────────────────────────────────────────
//
// Flat tinted strip: a 3px leading rule carries the tier instead of a glow, so
// a list of reds stays readable.

export const RiskAlert: React.FC<{
  tier: FatigueRiskTier;
  title: string;
  body: string;
}> = ({ tier, title, body }) => (
  <div
    className={cn(
      'flex items-start gap-3 rounded-lg border border-l-3 bg-card p-4',
      tierBorderClass[tier],
      tier === 'red' ? 'border-l-destructive' : tier === 'amber' ? 'border-l-warning' : 'border-l-success',
    )}
  >
    <AlertTriangle className={cn('mt-0.5 size-4 shrink-0', tierTextClass[tier])} aria-hidden />
    <div className='min-w-0'>
      <p className={cn('text-sm font-semibold', tierTextClass[tier])}>{title}</p>
      <p className='mt-1 text-sm text-muted-foreground'>{body}</p>
    </div>
  </div>
);

// ── MetricRing ──────────────────────────────────────────────────────────────
// Static SVG ring — no animated stroke, no drop-shadow.

export const MetricRing: React.FC<{
  /** 0–100 normalised fill. */
  value: number;
  display: string;
  tier: FatigueRiskTier;
  size?: number;
}> = ({ value, display, tier, size = 84 }) => {
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(Math.max(value, 0), 100) / 100);

  return (
    <div className='relative shrink-0' style={{ width: size, height: size }}>
      <svg width={size} height={size} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill='none' stroke='var(--color-border)' strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke={tierVar[tier]}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className='absolute inset-0 flex items-center justify-center'>
        <span className='text-xl font-semibold tabular-nums'>{display}</span>
      </div>
    </div>
  );
};

// ── Sparkline ───────────────────────────────────────────────────────────────

export const Sparkline: React.FC<{
  values: number[];
  tier?: FatigueRiskTier;
  width?: number;
  height?: number;
}> = ({ values, tier = 'green', width = 64, height = 22 }) => {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / span) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className='shrink-0' aria-hidden>
      <polyline
        points={points}
        fill='none'
        stroke={tierVar[tier]}
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

// ── StatRow ─────────────────────────────────────────────────────────────────

export const StatRow: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className='flex items-baseline justify-between gap-3 border-b border-border py-1.5 last:border-b-0'>
    <span className='min-w-0 text-sm text-muted-foreground'>
      {label}
      {hint && <span className='block text-xs text-muted-foreground/80'>{hint}</span>}
    </span>
    <span className='shrink-0 text-sm font-semibold tabular-nums'>{value}</span>
  </div>
);

// ── RagRangeTable (FM-16) ───────────────────────────────────────────────────
//
// The RAG bands behind a metric's badge, shown inside its breakdown. Bands are
// provisional until the fatigue lead confirms them (PEAK-185) — the caller
// renders RAG_PROVISIONAL_NOTE alongside.

export const RagRangeTable: React.FC<{
  metricKey: DerivedMetricKey;
  /** Highlights the band the current value falls in. */
  currentValue?: number;
}> = ({ metricKey, currentValue }) => {
  const { unit, bands } = RAG_RANGES[metricKey];

  return (
    <ul className='flex flex-col gap-1'>
      {bands.map((band) => {
        const active =
          currentValue !== undefined && currentValue >= band.from && (band.to === null || currentValue <= band.to);
        return (
          <li
            key={band.tier}
            className={cn(
              'flex items-center justify-between gap-3 rounded-md border px-2.5 py-1.5 text-sm',
              active ? cn(tierBorderClass[band.tier], tierSurfaceClass[band.tier]) : 'border-border',
            )}
          >
            <span className='flex items-center gap-2'>
              <span className={cn('size-2 rounded-full', tierDotClass[band.tier])} />
              <span className={cn('font-medium', active && tierTextClass[band.tier])}>{TIER_LABEL[band.tier]}</span>
              {active && <span className='text-xs text-muted-foreground'>current</span>}
            </span>
            <span className='tabular-nums text-muted-foreground'>
              {formatBand(band)} <span className='text-xs'>{unit}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// ── Small shared bits ───────────────────────────────────────────────────────

/** Skeleton block for loading states. */
export const LoadingBlock: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cn('animate-pulse rounded-lg bg-muted', className ?? 'h-40')} />
);

/** Inline note with an info glyph, for caveats and scope reminders. */
export const NoteLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className='flex items-start gap-2 text-xs text-muted-foreground'>
    <Info className='mt-0.5 size-3.5 shrink-0' aria-hidden />
    <span>{children}</span>
  </p>
);
